import React, { useState, useEffect } from "react";
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
} from "react-simple-maps";
import { Tooltip } from "react-tooltip";
import api from "../../api";
import "react-tooltip/dist/react-tooltip.css";
import ChartInfoPopup from "./ChartInfoPopup";

const geoUrl =
  "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

const UserPresenceMap = () => {
  const [markers, setMarkers] = useState([]);
  const [isDarkMode, setIsDarkMode] = useState(false);

  const token =
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("access_token");

  // Detect dark mode
  useEffect(() => {
    const checkDarkMode = () => {
      const dark = document.body.classList.contains("dark-mode");
      setIsDarkMode(dark);
      console.log("Dark Mode:", dark);
    };

    checkDarkMode();

    const observer = new MutationObserver(checkDarkMode);

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, []);

  // Fetch user presence
  useEffect(() => {
    const fetchPresence = async () => {
      try {
        const response = await api.get(
          `${process.env.REACT_APP_API_URL}/auth/user-presence`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        console.log("API Response:", response.data);

        const users = response.data.users || [];
        const countryMap = new Map();

        users.forEach((user) => {
          user.active_sessions.forEach((session) => {
            if (
              session.latitude &&
              session.longitude &&
              session.country
            ) {
              const key = session.country;

              if (!countryMap.has(key)) {
                countryMap.set(key, {
                  country: session.country,
                  coordinates: [
                    parseFloat(session.longitude),
                    parseFloat(session.latitude),
                  ],
                  count: 0,
                  visitors: new Set(),
                });
              }

              const entry = countryMap.get(key);
              entry.count += 1;
              entry.visitors.add(user.name);
            }
          });
        });

        const aggregatedMarkers = Array.from(
          countryMap.values()
        ).map((entry) => ({
          ...entry,
          visitors: Array.from(entry.visitors),
        }));

        console.log("Markers Data:", aggregatedMarkers);

        setMarkers(aggregatedMarkers);
      } catch (error) {
        console.error(
          "Failed to fetch user presence:",
          error
        );
      }
    };

    fetchPresence();
  }, [token]);

  // Watch marker updates
  useEffect(() => {
    console.log("Rendered Markers:", markers);
  }, [markers]);

  return (
    <div className="chart-card p-3 shadow-sm rounded mb-4 profile-i d-flex flex-column">
      <div className="d-flex align-items-center gap-2 mb-3">
        <h4 className="fw-bold mb-0">
          <span className="border-start border-4 ps-2 ak-text-lg d-flex align-content-center refresh-prompts">
            User Presence
          </span>
        </h4>

        <div className="chart-info-anchor">
          <ChartInfoPopup text="This world map illustrates where users are interacting with the chatbot." />
        </div>
      </div>

      <div
        style={{
          height: "300px",
          width: "100%",
          marginTop: "auto",
        }}
      >
        <ComposableMap
          projectionConfig={{ scale: 160 }}
          width={800}
          height={500}
          style={{
            width: "100%",
            height: "100%",
            outline: "none",
          }}
        >
          <Geographies geography={geoUrl}>
            {({ geographies }) =>
              geographies.map((geo) => (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  style={{
                    default: {
                      fill: isDarkMode
                        ? "#444444"
                        : "#ECEFF1",
                      stroke: isDarkMode
                        ? "#888888"
                        : "#607D8B",
                      strokeWidth: 0.5,
                    },
                    hover: {
                      fill: isDarkMode
                        ? "#555555"
                        : "#CFD8DC",
                      stroke: isDarkMode
                        ? "#BBBBBB"
                        : "#607D8B",
                      strokeWidth: 1,
                    },
                    pressed: {
                      fill: isDarkMode
                        ? "#666666"
                        : "#B0BEC5",
                    },
                  }}
                />
              ))
            }
          </Geographies>

          {markers.map(
            ({ country, coordinates, count }) => {
              console.log(
                "Rendering Marker:",
                country,
                coordinates
              );

              return (
                <Marker
                  key={country}
                  coordinates={coordinates}
                  data-tooltip-id="user-tooltip"
                  data-tooltip-content={`${country} - ${count} visitor(s)`}
                >
                  <circle
                    r={8}
                    fill={
                      isDarkMode
                        ? "#ff1744"
                        : "#FF5722"
                    }
                    stroke="#ffffff"
                    strokeWidth={2}
                  />
                </Marker>
              );
            }
          )}
        </ComposableMap>

        <Tooltip id="user-tooltip" />
      </div>
    </div>
  );
};

export default UserPresenceMap;
